import { useLazyQuery } from '@apollo/client'
import { Download, UploadFile, Visibility } from '@mui/icons-material'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { downloadDocument } from 'candidate/graphql/profile.gql'
import { useState } from 'react'
import ResumePreviewDialog, {
    isPdfDocument,
} from 'shared/components/ResumePreviewDialog'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'

interface DownloadResumeProps {
    documents: {
        documentKey: string
        documentType?: string | null
        id: string
    }[]
}

export default function DownloadResume(props: DownloadResumeProps) {
    const toast = useToast()

    const [download] = useLazyQuery(downloadDocument, {
        fetchPolicy: 'network-only',
    })

    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [previewLoading, setPreviewLoading] = useState(false)

    const resume = props.documents?.find((doc) => doc.documentType === 'Resume')

    const handleDownloadClick = async (id: string) => {
        const { data } = await download({ variables: { documentId: id } })
        if (data?.download?.url) {
            const hiddenDownloadLink = document.createElement('a')
            hiddenDownloadLink.href = data.download.url
            hiddenDownloadLink.target = '_blank'
            hiddenDownloadLink.click()
            hiddenDownloadLink.remove()
        } else {
            toast({
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        }
    }

    const handlePreviewClick = async (id: string) => {
        setPreviewUrl(null)
        setPreviewLoading(true)
        setPreviewOpen(true)
        try {
            const { data } = await download({
                variables: { documentId: id, forceInline: true },
            })
            setPreviewUrl(data?.download?.url ?? null)
        } catch {
            setPreviewUrl(null)
        }
        setPreviewLoading(false)
    }

    if (!resume) {
        return 'No resume available'
    }

    const fileName = formatDocumentKey(resume.documentKey)
    const canPreview = isPdfDocument(resume.documentKey)

    return (
        <>
            <Stack direction="row" alignItems="center" gap={getPxAsRem(30)}>
                <UploadFile color="primary" fontSize="medium" />
                <Typography variant="body1">
                    {fileName || 'No resume available.'}
                </Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
                {canPreview && (
                    <Tooltip title="Preview">
                        <IconButton
                            size="small"
                            onClick={() => handlePreviewClick(resume.id || '')}
                        >
                            <Visibility fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="Download">
                    <IconButton
                        size="small"
                        onClick={() => handleDownloadClick(resume.id || '')}
                    >
                        <Download fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
            <ResumePreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                url={previewUrl}
                fileName={fileName}
                loading={previewLoading}
            />
        </>
    )
}

const formatDocumentKey = (docKey: string) => {
    // remove the folder path and candidateId from the document key (if present, seed data doesn't have it)
    if (docKey.includes('/')) {
        const splitDocKey = docKey.split('/')
        return splitDocKey[splitDocKey.length - 1]
    } else {
        return docKey
    }
}
